import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { getTeacherAssignments } from '../../services/lessonService';
import { StudentPracticeProgressDialog } from './StudentPracticeProgressDialog';

interface Assignment {
  assignment_id: string;
  module_id: string;
  module_title: string;
  module_subject: string;
  module_grade_level: string;
  student_id: string;
  student_name: string;
  student_email: string;
  status: string;
  due_date: string | null;
  assigned_at: string;
  completed_at: string | null;
  updated_at: string;
  instructions: string | null;
}

interface ModuleGroup {
  module_id: string;
  module_title: string;
  module_subject: string;
  module_grade_level: string;
  assignments: Assignment[];
}

export const Assignments: React.FC = () => {
  const [modules, setModules] = useState<ModuleGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModule, setSelectedModule] = useState<ModuleGroup | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [progressStudent, setProgressStudent] = useState<Assignment | null>(null);

  useEffect(() => {
    loadAssignments();
  }, []);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTeacherAssignments('me');
      setModules(data.modules);
    } catch (err: any) {
      setError(err.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (module: ModuleGroup) => {
    setSelectedModule(module);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedModule(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'submitted':
        return 'info';
      case 'assigned':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      case 'submitted':
        return 'Submitted';
      case 'assigned':
        return 'Not Started';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getModuleStats = (module: ModuleGroup) => {
    const total = module.assignments.length;
    const completed = module.assignments.filter((a) => a.status === 'completed').length;
    const inProgress = module.assignments.filter((a) => a.status === 'in_progress').length;
    const notStarted = module.assignments.filter((a) => a.status === 'assigned').length;

    return { total, completed, inProgress, notStarted };
  };

  const filteredModules = modules.filter(
    (module) =>
      module.module_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      module.module_subject.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        My Assignments
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        View all modules you have assigned to students
      </Typography>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by module title or subject..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Modules Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Module</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Grade</TableCell>
              <TableCell align="center">Total Students</TableCell>
              <TableCell align="center">Completed</TableCell>
              <TableCell align="center">In Progress</TableCell>
              <TableCell align="center">Not Started</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredModules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" py={3}>
                    {searchTerm
                      ? 'No modules found matching your search'
                      : 'No assignments yet. Assign modules to students to see them here.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredModules.map((module) => {
                const stats = getModuleStats(module);
                return (
                  <TableRow key={module.module_id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {module.module_title}
                      </Typography>
                    </TableCell>
                    <TableCell>{module.module_subject}</TableCell>
                    <TableCell>{module.module_grade_level}</TableCell>
                    <TableCell align="center">
                      <Chip label={stats.total} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={stats.completed} color="success" size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={stats.inProgress} color="warning" size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={stats.notStarted} color="default" size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewDetails(module)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedModule?.module_title}
          <Typography variant="body2" color="text.secondary">
            {selectedModule?.module_subject} • {selectedModule?.module_grade_level}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedModule && (
            <List>
              {selectedModule.assignments.map((assignment, index) => (
                <React.Fragment key={assignment.assignment_id}>
                  {index > 0 && <Divider />}
                  <ListItem
                    secondaryAction={
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => setProgressStudent(assignment)}
                      >
                        View progress
                      </Button>
                    }
                  >
                    <ListItemText
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                      primary={
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Typography variant="body1">{assignment.student_name}</Typography>
                          <Chip
                            label={getStatusLabel(assignment.status)}
                            color={getStatusColor(assignment.status)}
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <Box mt={1}>
                          <Typography variant="body2" color="text.secondary">
                            Email: {assignment.student_email}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Assigned: {formatDate(assignment.assigned_at)}
                          </Typography>
                          {assignment.completed_at && (
                            <Typography variant="body2" color="text.secondary">
                              Completed: {formatDate(assignment.completed_at)}
                            </Typography>
                          )}
                          {assignment.due_date && (
                            <Typography variant="body2" color="text.secondary">
                              Due: {formatDate(assignment.due_date)}
                            </Typography>
                          )}
                          {assignment.instructions && (
                            <Typography variant="body2" color="text.secondary" mt={1}>
                              Instructions: {assignment.instructions}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>
      <StudentPracticeProgressDialog
        open={progressStudent !== null}
        studentId={progressStudent?.student_id || null}
        studentName={progressStudent?.student_name}
        onClose={() => setProgressStudent(null)}
      />
    </Box>
  );
};

export default Assignments;
